export const BATCH_SIZE = 10;
export const DEBOUNCE_DELAY = 300;

export function createBatches<T>(array: T[], batchSize: number = BATCH_SIZE): T[][] {
  const batches: T[][] = [];
  for (let i = 0; i < array.length; i += batchSize) {
    batches.push(array.slice(i, i + batchSize));
  }
  return batches;
}

export function createDelay(ms: number = 50): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}


class AttachmentCache {
  private cache = new Map<number, { imageCount: number; documentCount: number; timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  set(taskId: number, imageCount: number, documentCount: number): void {
    this.cache.set(taskId, {
      imageCount,
      documentCount,
      timestamp: Date.now()
    });
  }

  get(taskId: number): { imageCount: number; documentCount: number } | null {
    const cached = this.cache.get(taskId);
    if (!cached) return null;

    // Check if cache is still valid
    if (Date.now() - cached.timestamp > this.CACHE_DURATION) {
      this.cache.delete(taskId);
      return null;
    }

    return {
      imageCount: cached.imageCount,
      documentCount: cached.documentCount
    };
  }

  clear(): void {
    this.cache.clear();
  }

  has(taskId: number): boolean {
    return this.cache.has(taskId) && this.get(taskId) !== null;
  }
}

export const attachmentCache = new AttachmentCache();

/**
 * Optimized attachment fetching with caching and error handling
 */
export async function fetchAttachmentsWithCache(
  taskId: number,
  getImages: (id: number) => Promise<any[]>,
  getDocuments: (id: number) => Promise<any[]>
): Promise<{ imageCount: number; documentCount: number; totalAttachments: number }> {
  // Check cache first
  const cached = attachmentCache.get(taskId);
  if (cached) {
    return {
      ...cached,
      totalAttachments: cached.imageCount + cached.documentCount
    };
  }

  let imageCount = 0;
  let documentCount = 0;

  try {
    const [images, documents] = await Promise.all([
      getImages(taskId).catch(() => []),
      getDocuments(taskId).catch(() => [])
    ]);

    imageCount = Array.isArray(images) ? images.length : 0;
    documentCount = Array.isArray(documents) ? documents.length : 0;

    // Cache the result
    attachmentCache.set(taskId, imageCount, documentCount);
  } catch (error) {
    console.warn(`Failed to fetch attachments for task ${taskId}:`, error);
    // Return zeros on error, don't cache
  }

  return {
    imageCount,
    documentCount,
    totalAttachments: imageCount + documentCount
  };
}


export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
}

export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private timers = new Map<string, number>();

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  start(label: string): void {
    this.timers.set(label, performance.now());
  }

  end(label: string): number {
    const startTime = this.timers.get(label);
    if (!startTime) {
      console.warn(`No timer found for label: ${label}`);
      return 0;
    }

    const duration = performance.now() - startTime;
    this.timers.delete(label);
    
    console.log(`⏱️ ${label}: ${duration.toFixed(2)}ms`);
    return duration;
  }

  clear(): void {
    this.timers.clear();
  }
}

export const performanceMonitor = PerformanceMonitor.getInstance();

export interface VirtualScrollConfig {
  itemHeight: number;
  containerHeight: number;
  overscan?: number;
}

export function calculateVisibleRange(
  scrollTop: number,
  totalItems: number,
  config: VirtualScrollConfig
): { startIndex: number; endIndex: number; visibleItems: number } {
  const { itemHeight, containerHeight, overscan = 5 } = config;
  
  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
  const visibleItems = Math.ceil(containerHeight / itemHeight);
  const endIndex = Math.min(totalItems - 1, startIndex + visibleItems + overscan);

  return { startIndex, endIndex, visibleItems };
}
